import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-31d96c24"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "editor" }

import { onMounted, onUnmounted, ref } from 'vue'
import { debounce } from 'lodash'
import { useMainStore } from '@/store'
import type { EditorView } from 'prosemirror-view'
import { initProsemirrorEditor, createDocument } from '@/utils/prosemirror'
import { addMark, autoSelectAll, getTextAttrs, type TextAttrs } from '@/utils/prosemirror/utils'
import { toggleList } from '@/utils/prosemirror/commands/toggleList'
import tippy, { type Instance } from 'tippy.js'

import ColorPicker from '@/components/ColorPicker/index.vue'
import Popover from '@/components/Popover.vue'
import { toggleMark } from 'prosemirror-commands'


export default /*#__PURE__*/_defineComponent({
  __name: 'Editor',
  props: {
    value: {}
  },
  emits: ["update"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const mainStore = useMainStore()

const editorViewRef = ref<HTMLElement>()
let editorView: EditorView

const attr = ref<TextAttrs>()

const menuInstance = ref<Instance>()
const menuRef = ref<HTMLElement>()

const hideMenuInstance = () => {
  if (menuInstance.value) menuInstance.value.hide()
}

const handleInput = debounce(function() {
  emit('update', editorView.dom.innerHTML)
}, 300, { trailing: true })

const handleFocus = () => {
  mainStore.setDisableHotkeysState(true)
}

const handleBlur = () => {
  mainStore.setDisableHotkeysState(false)
}

const updateTextContent = () => {
  if (!editorView) return
  const { doc, tr } = editorView.state
  editorView.dispatch(tr.replaceRangeWith(0, doc.content.size, createDocument(props.value)))
}

__expose({ updateTextContent })

const handleMouseup = () => {
  const selection = window.getSelection()

  if (
    !selection ||
    !selection.anchorNode ||
    !selection.focusNode ||
    selection.isCollapsed ||
    selection.type === 'Caret' ||
    selection.type === 'None'
  ) return

  const range = selection.getRangeAt(0)

  if (menuInstance.value) {
    attr.value = getTextAttrs(editorView)

    const { x, y, left, top } = range.getBoundingClientRect()

    menuInstance.value.setProps({
      getReferenceClientRect: () => ({
        x, y, left, top,
        height: 0,
        width: 0,
        right: left,
        bottom: top,
      } as DOMRect),
    })
    menuInstance.value.show()
  }
}

const execCommand = (command: string, value?: string) => {
  if (command === 'color' && value) {
    const mark = editorView.state.schema.marks.forecolor.create({ color: value })
    autoSelectAll(editorView)
    addMark(editorView, mark)
  }
  else if (command === 'backcolor' && value) {
    const mark = editorView.state.schema.marks.backcolor.create({ backcolor: value })
    autoSelectAll(editorView)
    addMark(editorView, mark)
  }
  else if (command === 'bold') {
    autoSelectAll(editorView)
    toggleMark(editorView.state.schema.marks.strong)(editorView.state, editorView.dispatch)
  }
  else if (command === 'em') {
    autoSelectAll(editorView)
    toggleMark(editorView.state.schema.marks.em)(editorView.state, editorView.dispatch)
  }
  else if (command === 'underline') {
    autoSelectAll(editorView)
    toggleMark(editorView.state.schema.marks.underline)(editorView.state, editorView.dispatch)
  }
  else if (command === 'strikethrough') {
    autoSelectAll(editorView)
    toggleMark(editorView.state.schema.marks.strikethrough)(editorView.state, editorView.dispatch)
  }
  else if (command === 'bulletList') {
    const { bullet_list: bulletList, list_item: listItem } = editorView.state.schema.nodes
    toggleList(bulletList, listItem, '')(editorView.state, editorView.dispatch)
  }
  else if (command === 'orderedList') {
    const { ordered_list: orderedList, list_item: listItem } = editorView.state.schema.nodes
    toggleList(orderedList, listItem, '')(editorView.state, editorView.dispatch)
  }
  else if (command === 'clear') {
    autoSelectAll(editorView)
    const { $from, $to } = editorView.state.selection
    editorView.dispatch(editorView.state.tr.removeMark($from.pos, $to.pos))
  }

  editorView.focus()
  handleInput()
  attr.value = getTextAttrs(editorView)
}

onMounted(() => {
  editorView = initProsemirrorEditor((editorViewRef.value as Element), props.value, {
    handleDOMEvents: {
      focus: handleFocus,
      blur: handleBlur,
      mouseup: handleMouseup,
      mousedown: () => {
        window.getSelection()?.removeAllRanges()
        hideMenuInstance()
      },
      keydown: hideMenuInstance,
      input: handleInput,
    },
  }, {
    placeholder: '点击输入演讲者备注',
  })

  menuInstance.value = tippy(editorViewRef.value!, {
    duration: 0,
    content: menuRef.value!,
    interactive: true,
    trigger: 'manual',
    placement: 'top-start',
    hideOnClick: 'toggle',
    offset: [0, 6],
  })
})

onUnmounted(() => {
  editorView && editorView.destroy()
})

return (_ctx: any,_cache: any) => {
  const _component_IconTextBold = _resolveComponent("IconTextBold")!
  const _component_IconTextItalic = _resolveComponent("IconTextItalic")!
  const _component_IconTextUnderline = _resolveComponent("IconTextUnderline")!
  const _component_IconStrikethrough = _resolveComponent("IconStrikethrough")!
  const _component_IconText = _resolveComponent("IconText")!
  const _component_IconHighLight = _resolveComponent("IconHighLight")!
  const _component_IconList = _resolveComponent("IconList")!
  const _component_IconOrderedList = _resolveComponent("IconOrderedList")!
  const _component_IconFormat = _resolveComponent("IconFormat")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "prosemirror-editor",
      ref_key: "editorViewRef",
      ref: editorViewRef
    }, null, 512),
    _createElementVNode("div", {
      class: "menu",
      ref_key: "menuRef",
      ref: menuRef
    }, [
      _createElementVNode("button", {
        class: _normalizeClass({ 'active': attr.value?.bold }),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (execCommand('bold')))
      }, [
        _createVNode(_component_IconTextBold)
      ], 2),
      _createElementVNode("button", {
        class: _normalizeClass({ 'active': attr.value?.em }),
        onClick: _cache[1] || (_cache[1] = ($event: any) => (execCommand('em')))
      }, [
        _createVNode(_component_IconTextItalic)
      ], 2),
      _createElementVNode("button", {
        class: _normalizeClass({ 'active': attr.value?.underline }),
        onClick: _cache[2] || (_cache[2] = ($event: any) => (execCommand('underline')))
      }, [
        _createVNode(_component_IconTextUnderline)
      ], 2),
      _createElementVNode("button", {
        class: _normalizeClass({ 'active': attr.value?.strikethrough }),
        onClick: _cache[3] || (_cache[3] = ($event: any) => (execCommand('strikethrough')))
      }, [
        _createVNode(_component_IconStrikethrough)
      ], 2),
      _createVNode(Popover, {
        trigger: "click",
        style: {"width":"30%"}
      }, {
        content: _withCtx(() => [
          _createVNode(ColorPicker, {
            modelValue: attr.value?.color,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = value => execCommand('color', value))
          }, null, 8, ["modelValue"])
        ]),
        default: _withCtx(() => [
          _createElementVNode("button", null, [
            _createVNode(_component_IconText)
          ])
        ]),
        _: 1
      }),
      _createVNode(Popover, {
        trigger: "click",
        style: {"width":"30%"}
      }, {
        content: _withCtx(() => [
          _createVNode(ColorPicker, {
            modelValue: attr.value?.backcolor,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = value => execCommand('backcolor', value))
          }, null, 8, ["modelValue"])
        ]),
        default: _withCtx(() => [
          _createElementVNode("button", null, [
            _createVNode(_component_IconHighLight)
          ])
        ]),
        _: 1
      }),
      _createElementVNode("button", {
        class: _normalizeClass({ 'active': attr.value?.bulletList }),
        onClick: _cache[6] || (_cache[6] = ($event: any) => (execCommand('bulletList')))
      }, [
        _createVNode(_component_IconList)
      ], 2),
      _createElementVNode("button", {
        class: _normalizeClass({ 'active': attr.value?.orderedList }),
        onClick: _cache[7] || (_cache[7] = ($event: any) => (execCommand('orderedList')))
      }, [
        _createVNode(_component_IconOrderedList)
      ], 2),
      _createElementVNode("button", {
        onClick: _cache[8] || (_cache[8] = ($event: any) => (execCommand('clear')))
      }, [
        _createVNode(_component_IconFormat)
      ])
    ], 512)
  ])), [
    [_directive_click_outside, hideMenuInstance]
  ])
}
}

})